import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DicaFaixa.css';
import { DICAS, proximaDica, registrarVista, Dica } from '../utils/dicas';
import { revelarAlvo } from '../utils/revelarAlvo';

/**
 * A faixa que ensina o app, uma frase por vez.
 *
 * ── Onde o estado mora, e por quê ─────────────────────────────────────────
 *
 * `localStorage`, junto de `wallet-board-order` e `wallet-theme`. Não vai
 * para o banco: é preferência de tela, e o WhatsApp não tem faixa. Mandar
 * isto para o servidor custaria uma rota e uma migração para guardar quais
 * frases ela já leu.
 *
 * Uma chave só: quais frases já apareceram. Ela decide QUAL mostrar.
 *
 * ── O X não é guardado, e isso mudou depois de ela usar ───────────────────
 *
 * A primeira versão também gravava o DIA em que ela fechou, e escondia a
 * faixa até o dia seguinte. O relato veio na hora: "apertei o x e sumiu, mas
 * dei f5, saí e reloguei e não voltou". Três tentativas de trazer de volta é
 * uma pessoa dizendo que um dia é tempo demais.
 *
 * Agora o X vale enquanto a página estiver aberta — estado do componente,
 * nada gravado. Recarregar traz a próxima. Continua não sendo "a próxima
 * entra na hora", que foi a opção recusada: dentro da mesma tela ela sai e
 * fica fora.
 *
 * ── O ciclo, e a decisão de não aposentar ─────────────────────────────────
 *
 * Vistas as disponíveis, a fila recomeça. Foi escolha explícita: "é a A, mas
 * a faixa nunca se aposenta". A regra mora em `utils/dicas.ts`.
 *
 * ── Por que filtra pelo alvo estar na tela ────────────────────────────────
 *
 * No celular o formulário completo não é montado, então `tf-paste-btn-wrap`
 * não existe. Oferecer "cole o print" ali acenderia o vazio — que é o modo
 * de falhar que `tutorialAlvos.test.ts` existe para impedir no tour, e que
 * nenhum teste pega em tempo de execução.
 */

const CHAVE_VISTAS = 'wallet-dicas-vistas';

const lerVistas = (): string[] => {
  try {
    const cru = localStorage.getItem(CHAVE_VISTAS);
    const v = cru ? JSON.parse(cru) : [];
    // Guarda contra lixo: uma chave estragada não pode derrubar o painel.
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const gravar = (chave: string, valor: string) => {
  try { localStorage.setItem(chave, valor); } catch { /* modo privado, aba sem storage */ }
};

/**
 * As dicas que podem aparecer AGORA.
 *
 * Sem alvo entra sempre — ela não depende de nada estar na tela.
 */
const disponiveisNoDom = (doc: Document): string[] =>
  DICAS.filter(d => d.alvo === null || doc.getElementById(d.alvo) !== null).map(d => d.id);

export default function DicaFaixa() {
  const [dica, setDica] = useState<Dica | null>(null);
  const cancelarPulso = useRef<() => void>(() => {});

  /*
   * A escolha acontece DEPOIS da primeira pintura, e num efeito.
   *
   * `disponiveisNoDom` pergunta ao DOM quem existe, e no primeiro render as
   * seções ainda não montaram — escolher ali descartaria quase todas as
   * dicas e sobraria a sem alvo. Um tick depois o quadro está de pé.
   */
  useEffect(() => {
    const t = setTimeout(() => {
      setDica(proximaDica(lerVistas(), disponiveisNoDom(document)));
    }, 400);
    return () => clearTimeout(t);
  }, []);

  // O pulso é cancelado no desmonte: classe pendurada num elemento que
  // ninguém está olhando vira dois destaques acesos na dica seguinte.
  useEffect(() => () => cancelarPulso.current(), []);

  const fechar = useCallback(() => {
    if (!dica) return;
    cancelarPulso.current();
    gravar(CHAVE_VISTAS, JSON.stringify(
      registrarVista(lerVistas(), dica.id, disponiveisNoDom(document)),
    ));
    setDica(null);
  }, [dica]);

  /*
   * Clicar na frase NÃO marca como vista.
   *
   * Ela clicou porque quis fazer aquilo, e pode voltar amanhã sem ter feito.
   * Marcar aqui gastaria a dica no momento em que ela estava dando certo. Só
   * o X — que é ela dizendo "já vi" — consome.
   */
  const ir = useCallback(() => {
    if (!dica?.alvo) return;
    cancelarPulso.current();
    cancelarPulso.current = revelarAlvo(dica.alvo);
  }, [dica]);

  if (!dica) return null;

  const clicavel = dica.alvo !== null;

  return (
    <div className="df" role="status">
      <button
        type="button"
        className={`df-frase${clicavel ? '' : ' df-sem-alvo'}`}
        onClick={clicavel ? ir : undefined}
        // Sem alvo não é botão para leitor de tela: anunciar "botão" numa
        // frase que não leva a lugar nenhum é prometer um clique que não há.
        aria-disabled={!clicavel}
        tabIndex={clicavel ? 0 : -1}
      >
        {dica.texto}
      </button>
      <button type="button" className="df-x" onClick={fechar} aria-label="Fechar dica">
        ×
      </button>
    </div>
  );
}
